import React, { useState, useEffect } from 'react';
import Login from './Login';
import SignUp from './SignUp';
import ForgotPassword from './ForgotPassword';
import { isSupabaseConfigured } from '../src/lib/supabaseClient';
import type { Lang } from '../utils/i18n';

/** 与 utils/i18n.Lang 一致（Login/SignUp 等扩展文案） */
export type AuthLang = Lang;

type AuthPanel = 'login' | 'signup' | 'forgot';

interface AuthModalProps {
  onAuthSuccess?: () => void;
  onClose?: () => void;
  language?: AuthLang;
  onLanguageChange?: (lang: AuthLang) => void;
  /** When opening from landing page: 'login' | 'signup' to show the correct tab */
  initialMode?: 'login' | 'signup';
  /** 与静态官网 index.html 的 theme（localStorage）及实验室内切换一致 */
  isDarkMode?: boolean;
}

const AuthModal: React.FC<AuthModalProps> = ({
  onAuthSuccess,
  onClose,
  language = 'en',
  onLanguageChange,
  initialMode = 'login',
  isDarkMode = true,
}) => {
  const [panel, setPanel] = useState<AuthPanel>(() => (initialMode === 'signup' ? 'signup' : 'login'));
  const [configError, setConfigError] = useState<string | null>(null);

  const handleLanguageToggle = () => {
    const cycle: AuthLang[] = ['en', 'zh'];
    const i = cycle.indexOf(language || 'en');
    const newLang = cycle[(i + 1) % cycle.length];
    if (onLanguageChange) {
      onLanguageChange(newLang);
    }
  };

  useEffect(() => {
    if (initialMode === 'signup') setPanel('signup');
    else if (initialMode === 'login') setPanel('login');
  }, [initialMode]);

  useEffect(() => {
    if (!isSupabaseConfigured()) {
      setConfigError(
        language === 'en'
          ? 'Supabase is not configured. Please set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in your .env.local file.'
          : 'Supabase 未配置。请在 .env.local 文件中设置 VITE_SUPABASE_URL 和 VITE_SUPABASE_ANON_KEY。'
      );
    }
  }, [language]);

  const isLogin = panel === 'login';

  return (
    <div
      className={`fixed inset-0 z-[1000] flex flex-col p-0 overflow-auto ${isDarkMode ? 'bg-black' : 'bg-zinc-50'}`}
    >
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          className={`absolute top-4 left-4 z-[1010] p-2 rounded-lg transition-colors ${
            isDarkMode ? 'text-zinc-400 hover:text-white hover:bg-white/10' : 'text-zinc-500 hover:text-zinc-900 hover:bg-zinc-200/80'
          }`}
          aria-label={language === 'zh' ? '关闭' : 'Close'}
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}

      <button
        type="button"
        onClick={handleLanguageToggle}
        className={`absolute top-4 right-4 z-[1010] px-3 py-1.5 text-sm font-medium transition-all rounded-lg border border-transparent focus:outline-none focus:ring-2 focus:ring-[#D7CDA1] ${
          isDarkMode
            ? 'text-zinc-400 hover:text-[#D7CDA1] hover:bg-zinc-800/50 hover:border-[#D7CDA1]/30 focus:ring-offset-2 focus:ring-offset-black'
            : 'text-zinc-600 hover:text-[#b45309] hover:bg-zinc-100 hover:border-[#D7CDA1]/40 focus:ring-offset-2 focus:ring-offset-zinc-50'
        }`}
        title={language === 'en' ? 'English' : '中文'}
        aria-label="Toggle language"
      >
        <span className="text-xs font-semibold">
          {language === 'en' ? 'EN' : '中'}
        </span>
      </button>

      <div className="flex-1 min-h-0 flex flex-col">
        {configError ? (
          <div className="flex-1 flex items-center justify-center p-8">
            <div
              className={`max-w-md w-full rounded-2xl border p-8 shadow-xl ${
                isDarkMode ? 'bg-zinc-900/90 border-zinc-800' : 'bg-white border-zinc-200'
              }`}
            >
              <div className={`mb-4 text-sm rounded-lg p-4 border ${isDarkMode ? 'text-red-400 bg-red-900/50 border-red-800' : 'text-red-700 bg-red-50 border-red-200'}`}>
                {configError}
              </div>
              <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-zinc-600'}`}>
                {language === 'en' ? 'Please configure Supabase to enable authentication.' : '请配置 Supabase 以启用认证功能。'}
              </p>
            </div>
          </div>
        ) : panel === 'forgot' ? (
          <div className="flex-1 min-h-0 overflow-auto">
            <ForgotPassword language={language} isDarkMode={isDarkMode} onBackToLogin={() => setPanel('login')} />
          </div>
        ) : isLogin ? (
          <div className="flex-1 min-h-0 overflow-auto">
            <Login
              language={language}
              isDarkMode={isDarkMode}
              onSuccess={onAuthSuccess}
              onForgotPassword={() => setPanel('forgot')}
              onSwitchToSignUp={() => setPanel('signup')}
            />
          </div>
        ) : (
          <div className="flex-1 min-h-0 overflow-auto">
            <SignUp language={language} isDarkMode={isDarkMode} onSuccess={onAuthSuccess} onSwitchToLogin={() => setPanel('login')} />
          </div>
        )}
      </div>
    </div>
  );
};

export default AuthModal;
